import { Instagram, Linkedin, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import gggLogo from "@/assets/logos/ggg-logo-final.webp";
import PlatformButton from "@/components/PlatformButton";
import NewsletterForm from "@/components/NewsletterForm";
import { NAV_LINKS, FOOTER_SECONDARY_LINKS, FOOTER_LEGAL_LINKS } from "@/data/navigation";
import { VENTURES } from "@/data/ventures";
import { SOCIAL_LINKS } from "@/data/socialLinks";

const Footer = () => {
  return (
    <footer className="relative z-10 border-t mt-24 overflow-hidden" style={{ borderColor: "var(--gg-subtle-divider)" }} data-testid="site-footer">
      <div className="page-shell pt-16 relative z-10">
        <div className="neu-panel p-8 md:p-10 mb-16 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <span className="eyebrow block mb-2">Stay Connected</span>
            <p className="font-cormorant text-[22px]" style={{ color: "var(--gg-ivory)" }}>Join our newsletter for considered updates.</p>
          </div>
          <NewsletterForm />
        </div>
      </div>

      <div className="page-shell py-10 grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10">
        <div className="md:col-span-1 flex flex-col gap-5">
          <Link to="/" data-testid="footer-home-logo-link" aria-label="Gopi Gadhvi Group — Home">
            <img src={gggLogo} alt="Gopi Gadhvi Group" className="footer-logo" />
          </Link>
          <p className="font-cormorant italic text-[18px]" style={{ color: "var(--gg-secondary-text)" }}>
            Building Trust. Creating Legacy.
          </p>
          <p className="text-small">Ahmedabad, Gujarat, India</p>
        </div>

        <div>
          <span className="eyebrow block mb-4">Navigate</span>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-body hover:text-[color:var(--gg-pearl)] transition-colors duration-200" data-testid={`footer-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            {FOOTER_SECONDARY_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-small hover:text-[color:var(--gg-pearl)] transition-colors duration-200" data-testid={`footer-secondary-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            {FOOTER_LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-small hover:text-[color:var(--gg-pearl)] transition-colors duration-200" data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <span className="eyebrow block mb-4">Brands</span>
          <ul className="space-y-3">
            {VENTURES.map((v) => (
              <li key={v.slug}>
                <Link to={`/ventures/${v.slug}`} className="text-body hover:text-[color:var(--gg-pearl)] transition-colors duration-200" data-testid={`footer-venture-${v.slug}`}>
                  {v.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <span className="eyebrow block mb-4">Social</span>
          <div className="grid grid-cols-3 gap-3" style={{ maxWidth: "280px" }}>
            <PlatformButton href={SOCIAL_LINKS.linkedin} label="LinkedIn" icon={Linkedin} testId="footer-social-linkedin" />
            <PlatformButton href={SOCIAL_LINKS.instagram} label="Instagram" icon={Instagram} testId="footer-social-instagram" />
            <PlatformButton href={SOCIAL_LINKS.youtube} label="YouTube" icon={Youtube} testId="footer-social-youtube" />
          </div>
        </div>
      </div>

      <div className="border-t relative z-10" style={{ borderColor: "var(--gg-subtle-divider)" }}>
        <div className="page-shell py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
          <p className="text-small" data-testid="footer-credit">
            Made with Love <span aria-hidden="true">❤️</span> By{" "}
            <a
              href="https://theartforest.com"
              target="_blank"
              rel="noopener noreferrer"
              className="link-quiet"
              data-testid="footer-credit-link"
            >
              The Art Forest
            </a>
          </p>
          <p className="text-small" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} Gopi Gadhvi Group. All Rights Reserved.
          </p>
          <p className="text-small md:text-right" data-testid="footer-msme">
            MSME Registered Enterprise &middot; UDYAM-GJ-01-0630464
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
